import { NgClass } from '@angular/common'
import { AfterViewInit, Component, ElementRef, OnChanges, OnDestroy, SimpleChanges, computed, effect, inject, input, output, signal, viewChild } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { SmsMessageType } from '@app/v2/shared/enums/sms-message.enum'
import { SmsInsertChip, SmsPreviewEvent, SmsUnknownInsertHover } from '@app/v2/shared/types/sms-editor.type'
import { SmsMessageModel } from '@app/v2/shared/types/sms-message.model'
import { SmsMessageRequiredValidator } from '@app/v2/shared/validators/sms-message-required.validator'
import { NgxTippyModule, NgxTippyProps } from 'ngx-tippy-wrapper'
import { Subscription } from 'rxjs'

/**
 * Shared editor for viewing and updating the SMS message sent to guests.
 * The count fills placeholders with sample guest values, the same way the backend fills real ones.
 */
@Component({
  selector: 'app-sms-editor',
  templateUrl: './sms-editor.component.html',
  host: { class: 'tw-block tw-w-full' },
  standalone: true,
  imports: [NgClass, NgxTippyModule]
})
export class SmsEditorComponent implements OnChanges, AfterViewInit, OnDestroy {
  private static readonly _UNDO_LIMIT = 100
  private static readonly _UNDO_PAUSE_MS = 400
  private static readonly _KNOWN_INSERT_CLASS = 'tw-text-[#1C64F2] dark:tw-text-blue-400'
  private static readonly _UNKNOWN_INSERT_CLASS = 'sms-unknown-insert tw-text-red-600 dark:tw-text-red-400'

  /** Form control that holds the SMS message. The editor owns its "required" rule. */
  public readonly control = input.required<UntypedFormControl>()

  /** Event that owns the message. Name and slug are inserted as plain text; guest fields stay as {{placeholders}}. */
  public readonly event = input<SmsPreviewEvent | null>(null)

  /** Confirmation keeps confirmation/QR inserts; invitation swaps those for one invitation link. */
  public readonly messageType = input<SmsMessageType>(SmsMessageType.CONFIRMATION)

  /** Default body from the parent. Used when the control is empty and on Reset to default. */
  public readonly defaultMessage = input('')

  /** True when the filled-in message starts with Arabic, so the preview should be RTL. */
  public readonly rtlChange = output<boolean>()

  private readonly _highlight = viewChild<ElementRef<HTMLElement>>('highlight')
  private readonly _messageBox = viewChild<ElementRef<HTMLTextAreaElement>>('messageBox')

  public readonly gsmSegmentLimit = SmsMessageModel.GSM_SINGLE_LIMIT
  public readonly unicodeSegmentLimit = SmsMessageModel.UNICODE_SINGLE_LIMIT

  /** Message as the guest receives it, with sample values in place of the placeholders. */
  public readonly resolvedText = computed(() => SmsMessageModel.resolveSamples(this._message(), this.messageType()))

  public readonly characterCount = computed(() => this._segmentInfo().length)
  public readonly characterLimit = computed(() => (this._segmentInfo().isUnicode ? SmsMessageModel.UNICODE_SINGLE_LIMIT : SmsMessageModel.GSM_SINGLE_LIMIT))
  public readonly segmentCount = computed(() => this._segmentInfo().segments)

  /** Preview is RTL only when the filled-in message starts with Arabic. */
  public readonly isRtl = computed(() => SmsMessageModel.startsWithArabic(this.resolvedText()))

  /** Editor RTL: first real letter is Arabic once inserts and the event name/slug are ignored. */
  public readonly editorRtl = computed(() => SmsMessageModel.isEditorRtl(this._message(), this.event()))

  public readonly insertChips = computed(() => SmsMessageModel.insertChips(this.messageType(), this.event()))

  /** Known `{{placeholders}}` painted blue and unknown ones red behind the textarea. */
  public readonly highlightedMessage = computed(() => this._highlightHtml(this._message(), this.messageType()))

  /** Tippy target while the pointer is over an unknown `{{insert}}`. */
  public readonly unknownHover = signal<SmsUnknownInsertHover | null>(null)

  public readonly unknownInsertTippyProps: NgxTippyProps = {
    theme: 'dark',
    placement: 'top',
    trigger: 'manual',
    showOnCreate: true,
    hideOnClick: false,
    appendTo: () => document.body
  }

  private readonly _authService = inject(AuthService)
  private readonly _message = signal('')
  private readonly _segmentInfo = computed(() => SmsMessageModel.segmentInfo(this.resolvedText()))
  private readonly _undo: string[] = []
  private readonly _redo: string[] = []
  private _baseline = ''
  private _appliedDefault = ''
  private _historyTimer: ReturnType<typeof setTimeout> | null = null
  private _hoverFrame: number | null = null
  private _hoverPoint: { x: number; y: number } | null = null
  private _controlSubscription: Subscription | null = null

  constructor() {
    effect(() => this.rtlChange.emit(this.isRtl()))
  }

  /**
   * USD per SMS from AuthService confirmation or invitation pricing.
   */
  get smsMessagePriceUsd(): number {
    const prices = this.messageType() === SmsMessageType.INVITATION ? this._authService.invitationUnitPriceUsd : this._authService.resendConfirmationUnitPriceUsd
    return prices.sms
  }

  /**
   * True when the empty message should show the required error.
   */
  get showRequiredError(): boolean {
    const control = this.control()
    return control.invalid && (control.touched || control.dirty)
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this._attachControl(changes['control'].previousValue)
      return
    }
    if (changes['event'] || changes['defaultMessage'] || changes['messageType']) this._refreshDefaultIfUnedited()
  }

  public ngAfterViewInit(): void {
    this._writeView(this._message())
  }

  public ngOnDestroy(): void {
    this._clearHistoryTimer()
    this._cancelHoverFrame()
    this._detachControl(this.control())
  }

  /**
   * Keeps the form in sync without rewriting the textarea, so Ctrl+Z can undo.
   * @param event Native input event from the textarea
   */
  public onTemplateInput(event: Event): void {
    const control = this.control()
    control.setValue((event.target as HTMLTextAreaElement).value)
    control.markAsDirty()
    this._queueHistory()
  }

  /**
   * Marks the message as touched so an empty value can show the required error.
   */
  public onEditorBlur(): void {
    this.control().markAsTouched()
  }

  /**
   * Backspace/Delete on `{{barcode}}` removes the whole insert. Ctrl/Cmd+Z undoes, Ctrl/Cmd+Y or Shift+Z redoes.
   * @param event Keyboard event from the textarea
   */
  public onEditorKeydown(event: KeyboardEvent): void {
    const isPlainDelete = !event.ctrlKey && !event.metaKey && !event.altKey && (event.key === 'Backspace' || event.key === 'Delete')
    if (isPlainDelete && this._deletePlaceholder(event.key === 'Backspace')) {
      event.preventDefault()
      return
    }
    if (!(event.ctrlKey || event.metaKey)) return
    const key = event.key.toLowerCase()
    const isRedo = key === 'y' || (key === 'z' && event.shiftKey)
    if (!isRedo && key !== 'z') return
    this._commitTyping()
    if (!(isRedo ? this._redo : this._undo).length) return
    event.preventDefault()
    if (isRedo) this._redoChange()
    else this._undoChange()
  }

  /**
   * Puts the body back to the default, with the event's name already filled in.
   */
  public resetMessage(): void {
    this._applyEdit(this._defaultBody())
  }

  /**
   * Shows a tippy when the pointer is over an unknown `{{insert}}`. Checks at most once per animation frame.
   * @param event Mouse move over the textarea
   */
  public onEditorMouseMove(event: MouseEvent): void {
    this._hoverPoint = { x: event.clientX, y: event.clientY }
    if (this._hoverFrame !== null) return
    this._hoverFrame = requestAnimationFrame(() => {
      this._hoverFrame = null
      if (this._hoverPoint) this._updateUnknownHover(this._hoverPoint.x, this._hoverPoint.y)
    })
  }

  /**
   * Hides the unknown-insert tippy.
   */
  public clearUnknownHoverTip(): void {
    this._cancelHoverFrame()
    this._hoverPoint = null
    this.unknownHover.set(null)
  }

  /**
   * Inserts the chip at the caret, or at the end when the textarea is not focused.
   * Links get a space before and after so the filled-in URL does not glue to nearby text.
   * @param chip Chip the planner clicked
   */
  public insertChip(chip: SmsInsertChip): void {
    const current = this.control().value ?? ''
    const box = this._messageBox()?.nativeElement
    const isFocused = !!box && document.activeElement === box
    const start = isFocused ? box.selectionStart : current.length
    const end = isFocused ? box.selectionEnd : current.length
    const text = chip.isLink ? this._paddedLink(chip.value, current, start, end) : chip.value
    this._replaceRange(start, end, text)
  }

  /**
   * Starts editing a control: adds the required rule, fills the default or rewrites old placeholders, and listens for outside changes.
   * @param previous Control the editor was bound to before, if any
   */
  private _attachControl(previous: UntypedFormControl | undefined): void {
    if (previous) this._detachControl(previous)
    const control = this.control()
    if (!control.hasValidator(SmsMessageRequiredValidator)) control.addValidators(SmsMessageRequiredValidator)
    const saved = control.value ?? ''
    const initial = saved ? SmsMessageModel.rewriteLegacyPlaceholders(saved, this.event()) : this._defaultBody()
    this._setQuietly(initial)
    control.updateValueAndValidity({ emitEvent: false })
    this._undo.length = 0
    this._redo.length = 0
    this._baseline = initial
    this._appliedDefault = this._defaultBody()
    this._controlSubscription = control.valueChanges.subscribe((value) => this._onControlValue(value ?? ''))
  }

  /**
   * Stops editing a control and removes the required rule this editor added.
   * @param control Control to release
   */
  private _detachControl(control: UntypedFormControl): void {
    this._controlSubscription?.unsubscribe()
    this._controlSubscription = null
    control.removeValidators(SmsMessageRequiredValidator)
    control.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Mirrors a new control value into the editor. The textarea is only rewritten when the planner is not typing in it.
   * @param value Message on the control
   */
  private _onControlValue(value: string): void {
    this._message.set(value)
    const box = this._messageBox()?.nativeElement
    if (!box || document.activeElement === box || box.value === value) return
    box.value = value
    this._baseline = value
  }

  /**
   * Writes a value the editor produced itself (default or rewrite) without notifying the parent.
   * @param value Message to store
   */
  private _setQuietly(value: string): void {
    const control = this.control()
    if ((control.value ?? '') !== value) control.setValue(value, { emitEvent: false })
    this._message.set(value)
    this._writeView(value)
  }

  /**
   * Rebuilds the default when the event or default changes, if the planner has not edited the message yet.
   */
  private _refreshDefaultIfUnedited(): void {
    const next = this._defaultBody()
    const current = this.control().value ?? ''
    if (this._appliedDefault && current === this._appliedDefault && next !== current) {
      this._setQuietly(next)
      this._baseline = next
    }
    this._appliedDefault = next
  }

  /**
   * Default body from the parent, or the built-in one for this message type.
   */
  private _defaultBody(): string {
    const defaultMessage = this.defaultMessage()
    if (defaultMessage) return SmsMessageModel.rewriteLegacyPlaceholders(defaultMessage, this.event())
    return SmsMessageModel.defaultBody(this.messageType(), this.event())
  }

  /**
   * Shows or hides the unknown-insert tippy for this pointer position.
   * @param clientX Pointer X
   * @param clientY Pointer Y
   */
  private _updateUnknownHover(clientX: number, clientY: number): void {
    const hit = this._unknownInsertAtPoint(clientX, clientY)
    const parent = this._highlight()?.nativeElement.parentElement
    if (!hit || !parent) {
      this.unknownHover.set(null)
      return
    }
    const rect = hit.getBoundingClientRect()
    const origin = parent.getBoundingClientRect()
    const next: SmsUnknownInsertHover = {
      tip: `We can't fill in ${hit.textContent}.`,
      left: rect.left - origin.left,
      top: rect.top - origin.top,
      width: rect.width,
      height: rect.height
    }
    const current = this.unknownHover()
    if (current && current.tip === next.tip && current.left === next.left && current.top === next.top) return
    this.unknownHover.set(next)
  }

  /**
   * Highlight span for an unknown insert under this point, if any.
   * @param clientX Pointer X
   * @param clientY Pointer Y
   */
  private _unknownInsertAtPoint(clientX: number, clientY: number): HTMLElement | null {
    const root = this._highlight()?.nativeElement
    if (!root) return null
    for (const node of Array.from(root.querySelectorAll<HTMLElement>('.sms-unknown-insert'))) {
      const rect = node.getBoundingClientRect()
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) return node
    }
    return null
  }

  /**
   * Removes `{{name}}` in one step when Backspace or Delete would hit any character inside it.
   * @param isBackspace True for Backspace, false for Delete
   */
  private _deletePlaceholder(isBackspace: boolean): boolean {
    const box = this._messageBox()?.nativeElement
    if (!box) return false
    const caretStart = box.selectionStart ?? 0
    const caretEnd = box.selectionEnd ?? caretStart
    if (caretStart !== caretEnd) return false
    const text = box.value
    const index = isBackspace ? caretStart - 1 : caretStart
    if (index < 0 || index >= text.length) return false
    const range = this._placeholderRange(text, index)
    if (!range) return false
    this._replaceRange(range.start, range.end, '')
    return true
  }

  /**
   * Complete `{{name}}` that contains this character index, including the braces.
   * @param text Message in the textarea
   * @param index Character the user is about to delete
   */
  private _placeholderRange(text: string, index: number): { start: number; end: number } | null {
    const start = text.lastIndexOf('{{', index)
    if (start < 0) return null
    const close = text.indexOf('}}', start + 2)
    if (close < 0 || index > close + 1) return null
    const key = text.slice(start + 2, close)
    if (!key.length || /[{}\s]/.test(key)) return null
    return { start, end: close + 2 }
  }

  /**
   * Surrounds a link insert with spaces unless a space or newline is already there.
   * @param text Link placeholder
   * @param current Message before insert
   * @param start Caret start
   * @param end Caret end
   */
  private _paddedLink(text: string, current: string, start: number, end: number): string {
    const before = start > 0 ? current[start - 1] : ''
    const after = end < current.length ? current[end] : ''
    const lead = before && !/\s/.test(before) ? ' ' : ''
    const trail = !after || !/\s/.test(after) ? ' ' : ''
    return `${lead}${text}${trail}`
  }

  /**
   * Replaces a span of the message and puts the caret after the new text.
   * @param start Inclusive index in the current message
   * @param end Exclusive index in the current message
   * @param token Text to write into that span
   */
  private _replaceRange(start: number, end: number, token: string): void {
    const current = this.control().value ?? ''
    this._applyEdit(`${current.slice(0, start)}${token}${current.slice(end)}`)
    queueMicrotask(() => {
      const box = this._messageBox()?.nativeElement
      box?.focus()
      box?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  /**
   * Groups typing into one undo step after a short pause.
   */
  private _queueHistory(): void {
    this._clearHistoryTimer()
    this._historyTimer = setTimeout(() => {
      this._historyTimer = null
      this._commitTyping()
    }, SmsEditorComponent._UNDO_PAUSE_MS)
  }

  /**
   * Stores the last pause in typing as an undo snapshot.
   */
  private _commitTyping(): void {
    const value = this.control().value ?? ''
    if (value === this._baseline) return
    this._pushUndo(this._baseline)
    this._baseline = value
    this._redo.length = 0
  }

  /**
   * Applies an edit made by the editor (insert, delete or reset) as its own undo step.
   * @param next Message after the edit
   */
  private _applyEdit(next: string): void {
    this._commitTyping()
    const current = this.control().value ?? ''
    if (current === next) return
    this._pushUndo(current)
    this._redo.length = 0
    this._applySnapshot(next)
  }

  /**
   * Restores the previous snapshot.
   */
  private _undoChange(): void {
    const previous = this._undo.pop()
    if (previous === undefined) return
    this._redo.push(this.control().value ?? '')
    this._applySnapshot(previous)
  }

  /**
   * Restores the snapshot undone last.
   */
  private _redoChange(): void {
    const next = this._redo.pop()
    if (next === undefined) return
    this._pushUndo(this.control().value ?? '')
    this._applySnapshot(next)
  }

  /**
   * Shows a history snapshot in the control and the textarea.
   * @param value Message to show
   */
  private _applySnapshot(value: string): void {
    this._baseline = value
    this.control().setValue(value)
    this._writeView(value)
  }

  /**
   * Pushes a snapshot onto the undo stack and drops the oldest when full.
   * @param value Message before the latest edit
   */
  private _pushUndo(value: string): void {
    this._undo.push(value)
    if (this._undo.length > SmsEditorComponent._UNDO_LIMIT) this._undo.shift()
  }

  /**
   * Copies the message into the textarea without going through formControl, which would clear native caret state.
   * @param value Message to show
   */
  private _writeView(value: string): void {
    const box = this._messageBox()?.nativeElement
    if (!box || box.value === value) return
    box.value = value
  }

  /**
   * Clears the typing-pause timer.
   */
  private _clearHistoryTimer(): void {
    if (!this._historyTimer) return
    clearTimeout(this._historyTimer)
    this._historyTimer = null
  }

  /**
   * Cancels a pending hover check.
   */
  private _cancelHoverFrame(): void {
    if (this._hoverFrame === null) return
    cancelAnimationFrame(this._hoverFrame)
    this._hoverFrame = null
  }

  /**
   * Escapes the message and wraps each `{{placeholder}}` in a colored span.
   * @param template Message in the editor
   * @param type Confirmation or invitation
   */
  private _highlightHtml(template: string, type: SmsMessageType): string {
    const known = SmsMessageModel.knownKeys(type)
    return this._escapeHtml(template).replace(SmsMessageModel.PLACEHOLDER_PATTERN, (match: string, key: string) => {
      const className = known.has(key) ? SmsEditorComponent._KNOWN_INSERT_CLASS : SmsEditorComponent._UNKNOWN_INSERT_CLASS
      return `<span class="${className}">${match}</span>`
    })
  }

  /**
   * Escapes planner text before it is painted as HTML.
   * @param value Raw message
   */
  private _escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }
}
